import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bus,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  StickyNote,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import {
  fetchBusTastingsForDispensary,
  requestBusTasting,
} from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { toast } from "sonner";

const STATUS_META = {
  requested: { label: "Requested",  color: "text-amber-200 bg-amber-500/10 border-amber-500/30",   icon: AlertCircle  },
  scheduled: { label: "Scheduled",  color: "text-emerald-200 bg-emerald-500/10 border-emerald-500/30", icon: Calendar },
  completed: { label: "Completed",  color: "text-zinc-300 bg-zinc-500/10 border-zinc-500/30",      icon: CheckCircle2 },
  cancelled: { label: "Cancelled",  color: "text-rose-300 bg-rose-500/10 border-rose-500/30",      icon: XCircle      },
};

function fmtTime(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${ampm}`;
}

/**
 * Bus Tasting strip on the Dispensary Detail page.
 *
 * Shows ALL tastings for this account (requested + scheduled + history),
 * plus a "Request Bus Tasting" button that lets the sales rep ask for one.
 * The dispatcher's view (where they actually schedule + complete) lives on
 * the dedicated `/bus-tastings` page.
 */
export function DispensaryBusTastings({ dispensaryId, dispensaryName }) {
  const [tastings, setTastings] = useState(null);
  const [showRequest, setShowRequest] = useState(false);

  const load = async () => {
    if (!dispensaryId) return;
    try {
      const list = await fetchBusTastingsForDispensary(dispensaryId);
      setTastings(list);
    } catch {
      setTastings([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispensaryId]);

  // A dispensary can only have ONE active (requested/scheduled) tasting at a time
  const hasActive = (tastings || []).some(
    (t) => t.status === "requested" || t.status === "scheduled"
  );

  const recent = (tastings || []).slice(0, 5);
  const completedCount = (tastings || []).filter((t) => t.status === "completed").length;

  return (
    <div className="panel p-5" data-testid="dispensary-bus-tastings">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-mono">
            <Bus className="w-3 h-3 text-emerald-300" />
            Bus Events
            {completedCount > 0 && (
              <span className="text-zinc-600 normal-case tracking-normal font-normal">
                · {completedCount} completed all-time
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-1.5 max-w-xl leading-relaxed">
            Request a tasting event here — the dispatcher will be notified and
            schedule a date with crew assignment.
          </p>
        </div>
        <Button
          data-testid="request-bus-tasting-btn"
          onClick={() => setShowRequest(true)}
          disabled={hasActive}
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          title={hasActive ? "This account already has an active bus event — wait until it's completed or cancelled." : "Request a bus event"}
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Request Bus Event
        </Button>
      </div>

      {hasActive && (
        <div className="mb-3 text-[11px] text-amber-300 inline-flex items-center gap-1.5">
          <AlertCircle className="w-3 h-3" />
          One active request/schedule at a time — finish or cancel it first.
        </div>
      )}

      {tastings === null ? (
        <div className="px-3 py-4 text-xs text-zinc-500">Loading…</div>
      ) : tastings.length === 0 ? (
        <div className="px-4 py-8 text-center text-xs text-zinc-500 border border-dashed border-zinc-800 rounded-md">
          <Bus className="w-6 h-6 mx-auto text-zinc-700 mb-2" />
          No bus events on file yet.
        </div>
      ) : (
        <div className="space-y-2">
          {recent.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.requested;
            const StatusIcon = meta.icon;
            return (
              <div
                key={t.id}
                data-testid={`tasting-history-${t.id}`}
                className="border border-zinc-800 rounded-md p-2.5 bg-zinc-950/40"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono uppercase tracking-wider ${meta.color}`}>
                      <StatusIcon className="w-2.5 h-2.5" />
                      {meta.label}
                    </span>
                    {(t.scheduled_date || t.requested_date) && (
                      <span className="font-mono text-zinc-300">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {t.scheduled_date || t.requested_date}
                      </span>
                    )}
                    {(t.scheduled_time || t.requested_time) && (
                      <span className="font-mono text-zinc-400">
                        <Clock className="w-3 h-3 inline mr-0.5" />
                        {fmtTime(t.scheduled_time || t.requested_time)}
                      </span>
                    )}
                  </div>
                  {(t.status === "requested" || t.status === "scheduled") && (
                    <Link
                      to="/bus-tastings"
                      data-testid={`open-in-dispatcher-${t.id}`}
                      className="text-[10px] text-emerald-300 hover:text-emerald-200 font-mono inline-flex items-center gap-1"
                    >
                      Manage in dispatcher
                      <ExternalLink className="w-2.5 h-2.5" />
                    </Link>
                  )}
                </div>
                {t.requested_notes && (
                  <div className="mt-1.5 text-xs text-zinc-400 italic leading-snug">
                    <StickyNote className="w-3 h-3 inline mr-1 text-zinc-600" />
                    {t.requested_notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showRequest && (
        <RequestDialog
          dispensaryId={dispensaryId}
          dispensaryName={dispensaryName}
          onClose={() => setShowRequest(false)}
          onSaved={() => { setShowRequest(false); load(); }}
        />
      )}
    </div>
  );
}

function RequestDialog({ dispensaryId, dispensaryName, onClose, onSaved }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    try {
      setSaving(true);
      await requestBusTasting(dispensaryId, {
        requested_date: date || null,
        requested_time: time || null,
        requested_notes: notes.trim(),
      });
      toast.success("Tasting requested — dispatcher will see it shortly");
      onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || String(e);
      toast.error("Could not submit request", { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bus className="w-5 h-5 text-emerald-400" />
            Request Bus Event · {dispensaryName}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-zinc-500 leading-relaxed">
          Tell the dispatcher when this dispensary wants the tasting. They'll
          confirm the date + assign a driver, hosts, and lead hosts.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Requested date</Label>
            <Input
              data-testid="request-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Requested time</Label>
            <Input
              data-testid="request-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 mt-1"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs text-zinc-400">
            Notes (what the shop is asking for)
          </Label>
          <Textarea
            data-testid="request-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Owner wants 4-bud variety + 2 vapes for staff…"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 text-xs font-mono"
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900"
          >
            Cancel
          </Button>
          <Button
            data-testid="request-submit"
            onClick={submit}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {saving ? "Submitting…" : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DispensaryBusTastings;
